import { useTranslation } from 'react-i18next';
import { ModalWithButtons } from '../../../../components/ModalWithButtons/ModalWithButtons.component';
import { ButtonOne } from '../../../../components/ButtonOne/ButtonOne.component';
import Grid from '@mui/material/Grid';
import { FormControlLabel, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import { CheckboxOne } from '../../../../components/CheckboxOne/CheckboxOne.component';
import { ChangeEvent, useEffect, useState } from 'react';
import { useStyles } from './AdvancedFilter.styles';
import Pipe from '../../../../assets/icons/pipe.svg';
import TooltipOneComponent from '../../../../components/TooltipOne/TooltipOne.component';
import ExpandIcon from '@mui/icons-material/AddBoxRounded';
import CollapseIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import { TripleSwitch } from '../../../../components/TripleSwitch/TripleSwitch.component';
import { AdvanceFilter, ExamTaskFilter, PointsFilterType } from './AdvanceFilter.types';
import { ExamTaskGroupSolutionWithTasks } from '../../../../models/assessment.types';
import { getExamTaskGroup } from '../../../../services/exams/examTaskGroup/examTaskGroup.service';
import { useLayoutContext } from '../../../../Context/Layout.context';

interface AdvancedFilterProps {
  advancedFilter?: AdvanceFilter | null;
  assessmentSessionName: string;
  onClose: () => void;
  isResetFilter: boolean;
  setIsResetAdvancedFilter: (value: boolean) => void;
  onAdvancedFilterReset: () => void;
  handleAdvancedFilterChange: (filter: AdvanceFilter | null) => void;
  checkedTasks: string[];
  setCheckedTasks: React.Dispatch<React.SetStateAction<string[]>>;
  checkedCriterions: string[];
  setCheckedCriterions: React.Dispatch<React.SetStateAction<string[]>>;
}

export const AdvancedFilterModal: React.FC<AdvancedFilterProps> = (props) => {
  const { t } = useTranslation();
  const { showLoader } = useLayoutContext();
  const classes = useStyles();
  const [expandedTasks, setExpandedTasks] = useState<string[]>([]);
  const [filter, setFilter] = useState<AdvanceFilter | null>(props.advancedFilter ?? null);
  const [examTasks, setExamTasks] = useState<ExamTaskGroupSolutionWithTasks>();
  const [emptyFields, setEmptyFields] = useState<string[]>([]);

  const errorMessage = 'Pole nie może być puste';

  useEffect(() => {
    if (!examTasks) return;
    if (!filter || props.isResetFilter) {
      if (examTasks.examTasksCriteria.length) {
        const initialFilter: AdvanceFilter = { examTasks: [] };
        examTasks.examTasksCriteria.map((task, index) => {
          initialFilter.examTasks.push({
            id: task.taskId,
            taskNumber: task.taskNumber,
            pointsFilterType: PointsFilterType.exactPonts,
            exactPoints: 0,
            pointsFrom: 0,
            pointsTo: 100,
            noAnswer: 0,
            examScoringSchemas: [],
          });
          if (task && task.examScoringSchemas) {
            task.examScoringSchemas.map((criterion) => {
              initialFilter.examTasks[index].examScoringSchemas.push({
                id: criterion.examScoringSchemaId,
                criterionNumber: criterion.label,
                pointsFilterType: PointsFilterType.exactPonts,
                exactPoints: 0,
                pointsFrom: 0,
                pointsTo: 100,
              });
            });
          }
        });
        props.setCheckedTasks([]);
        props.setCheckedCriterions([]);
        props.setIsResetAdvancedFilter(false);
        setExpandedTasks([]);
        setEmptyFields([]);
        setFilter(initialFilter);
      }
    }
  }, [examTasks, props.isResetFilter]);

  useEffect(() => {
    showLoader(true);
    getExamTaskGroup()
      .then((result: ExamTaskGroupSolutionWithTasks) => {
        setExamTasks(result);
      })
      .finally(() => showLoader(false));
  }, []);

  const isExpandedTask = (id: string) => {
    return expandedTasks.find((task) => task === id);
  };

  const handleExpandTask = (id: string) => {
    if (expandedTasks.indexOf(id) === -1) {
      setExpandedTasks((arr) => [...arr, id]);
    } else {
      setExpandedTasks(expandedTasks.filter((el: string) => el !== id));
    }
  };

  const isTaskChecked = (id: string) => {
    return props.checkedTasks.includes(id);
  };

  const isCriterionChecked = (id: string) => {
    return props.checkedCriterions.includes(id);
  };

  const handleTaskCheckboxChange = (id: string) => {
    if (props.checkedTasks.indexOf(id) === -1) {
      props.setCheckedTasks((prevState) => [...prevState, id]);
    } else props.setCheckedTasks(props.checkedTasks.filter((item) => item !== id));
  };

  const handleCriterionCheckboxChange = (id: string) => {
    if (props.checkedCriterions.indexOf(id) === -1) {
      props.setCheckedCriterions((prevState) => [...prevState, id]);
    } else props.setCheckedCriterions(props.checkedCriterions.filter((item) => item !== id));
  };

  const removeEmptyFields = (taskId: string, criterionId?: string) => {
    const fieldIds = [
      `${taskId}_exactPoints_${criterionId ? criterionId : ''}`,
      `${taskId}_pointsFrom_${criterionId ? criterionId : ''}`,
      `${taskId}_pointsTo_${criterionId ? criterionId : ''}`,
    ];
    if (emptyFields.length) {
      emptyFields.forEach((fieldId) => {
        if (fieldIds.includes(fieldId)) {
          setEmptyFields(emptyFields.filter((item: string) => item !== fieldId));
        }
      });
    }
  };

  const handleFilterTypeChange = (value: PointsFilterType, taskId: string, criterionId?: string) => {
    const taskIndex = filter?.examTasks.findIndex((task) => task.id === taskId);
    const criterionIndex =
      taskIndex !== undefined && taskIndex !== -1
        ? filter?.examTasks[taskIndex].examScoringSchemas.findIndex((criterion) => criterion.id === criterionId)
        : undefined;

    if (taskIndex !== undefined && taskIndex !== -1) {
      setFilter((prevState) => {
        if (!prevState) return prevState;
        return {
          ...prevState,
          examTasks: prevState.examTasks.map((task, tIdx) => {
            if (tIdx !== taskIndex) return task;
            if (criterionIndex === undefined || criterionIndex === -1) {
              return { ...task, pointsFilterType: value, exactPoints: 0, pointsFrom: 0, pointsTo: 100 };
            }
            return {
              ...task,
              examScoringSchemas: task.examScoringSchemas.map((criterion, cIdx) => {
                if (cIdx !== criterionIndex) return criterion;
                return { ...criterion, pointsFilterType: value, exactPoints: 0, pointsFrom: 0, pointsTo: 100 };
              }),
            };
          }),
        };
      });
      removeEmptyFields(taskId, criterionId);
    }
  };

  const handlePointsFilterChange = (value: string, type: string, taskId: string, criterionId?: string) => {
    const fieldId = `${taskId}_${type}_${criterionId ? criterionId : ''}`;
    if (value === '' && emptyFields.indexOf(fieldId) === -1) {
      setEmptyFields((prevState) => [...prevState, fieldId]);
    } else if (value !== '' && emptyFields.indexOf(fieldId) !== -1) {
      setEmptyFields(emptyFields.filter((item: string) => item !== fieldId));
    }

    const taskIndex = filter?.examTasks.findIndex((task) => task.id === taskId);
    const criterionIndex =
      taskIndex !== undefined && taskIndex !== -1
        ? filter?.examTasks[taskIndex].examScoringSchemas.findIndex((criterion) => criterion.id === criterionId)
        : undefined;
    if (taskIndex !== undefined) {
      setFilter((prevState) => {
        if (!prevState) return prevState;
        return {
          ...prevState,
          examTasks: prevState.examTasks.map((task, tIdx) => {
            if (tIdx !== taskIndex) return task;
            if (criterionIndex === undefined || criterionIndex === -1) {
              const parsed = parseInt(value);
              switch (type) {
                case 'exactPoints':
                  return { ...task, exactPoints: parsed, ...(value === '0' ? { noAnswer: 0 } : {}) };
                case 'pointsFrom':
                  return { ...task, pointsFrom: parsed };
                case 'pointsTo':
                  return { ...task, pointsTo: parsed };
                default:
                  return task;
              }
            }
            return {
              ...task,
              examScoringSchemas: task.examScoringSchemas.map((criterion, cIdx) => {
                if (cIdx !== criterionIndex) return criterion;
                const parsed = parseInt(value);
                switch (type) {
                  case 'exactPoints':
                    return { ...criterion, exactPoints: parsed };
                  case 'pointsFrom':
                    return { ...criterion, pointsFrom: parsed };
                  case 'pointsTo':
                    return { ...criterion, pointsTo: parsed };
                  default:
                    return criterion;
                }
              }),
            };
          }),
        };
      });
    }
  };

  const handleNoAnswerFilterChange = (value: number, id: string) => {
    const taskIndex = filter?.examTasks.findIndex((task) => task.id === id);

    if (taskIndex !== undefined && taskIndex !== -1) {
      setFilter((prevState) => {
        if (!prevState) return prevState;
        return {
          ...prevState,
          examTasks: prevState.examTasks.map((task, tIdx) =>
            tIdx !== taskIndex ? task : { ...task, noAnswer: value },
          ),
        };
      });
    }
  };

  const handleOnSave = () => {
    props.handleAdvancedFilterChange(filter);
  };

  const getPointsFilterTypeLabel = (pointsType: PointsFilterType) => {
    switch (pointsType) {
      case PointsFilterType.exactPonts:
        return 'Dokładna liczba punktów';
      case PointsFilterType.rangePonts:
        return 'Zakres punktów';
    }
  };

  return (
    <ModalWithButtons
      open
      size="md"
      autoHeight
      title={'Filtrowanie zaawansowane'}
      xCloseButton={props.onClose}
      onConfirm={() => handleOnSave()}
      confirmButtonDisabled={emptyFields.length > 0 || !props.checkedTasks.length}
      onCancel={props.onClose}
      confirmButtonLabel="Wyszukaj"
    >
      <Typography style={{ marginTop: '-10px', paddingBottom: '15px' }}>
        Sesja oceniania: <span style={{ fontWeight: 'bold' }}>{props.assessmentSessionName}</span>
      </Typography>
      <Grid container className={classes.container}>
        {filter &&
          filter.examTasks.map((task: ExamTaskFilter) => {
            return (
              <Grid xs={12} key={task.id}>
                <Grid xs={12} className={[classes.item, classes.itemHeaderRow].join(' ')}>
                  <Grid xs={6} className={classes.item}>
                    <CheckboxOne
                      fontSize={22}
                      key={task.id}
                      checked={isTaskChecked(task.id)}
                      onChange={() => handleTaskCheckboxChange(task.id)}
                    />
                    <Typography
                      style={{ marginRight: '30px', fontSize: '16px', fontWeight: 'bold' }}
                    >{`Zadanie ${task.taskNumber}`}</Typography>
                  </Grid>
                  {task.examScoringSchemas.length > 0 && isTaskChecked(task.id) && (
                    <Grid xs={1} className={classes.item}>
                      <TooltipOneComponent
                        placement="top"
                        title={isExpandedTask(task.id) ? 'Zwiń kryteria oceniania' : 'Rozwiń kryteria oceniania'}
                      >
                        <span
                          onClick={() => {
                            handleExpandTask(task.id);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {isExpandedTask(task.id) ? <CollapseIcon /> : <ExpandIcon />}
                        </span>
                      </TooltipOneComponent>
                    </Grid>
                  )}
                </Grid>
                {(filter.examTasks.length > 1 || props.checkedTasks.length > 0) && <hr className={classes.line} />}
                <Grid xs={12}>
                  <>
                    {isTaskChecked(task.id) && (
                      <>
                        <Grid xs={12} className={[classes.item, classes.itemWithRadioGroup].join(' ')}>
                          <Grid item xs={6}>
                            <RadioGroup
                              row
                              aria-label="wybór typu filtra"
                              name="filter-type-selector"
                              value={task.pointsFilterType}
                              onChange={(e) => {
                                handleFilterTypeChange(parseInt(e.target.value) as PointsFilterType, task.id);
                              }}
                            >
                              <FormControlLabel
                                value={PointsFilterType.exactPonts}
                                control={<Radio />}
                                label={getPointsFilterTypeLabel(PointsFilterType.exactPonts)}
                              />
                              <div style={{ display: 'flex' }}>
                                <img src={Pipe} style={{ margin: '3px 15px 3px 0px' }} alt="ikona linia pionowa" />{' '}
                              </div>
                              <FormControlLabel
                                value={PointsFilterType.rangePonts}
                                control={<Radio />}
                                label={getPointsFilterTypeLabel(PointsFilterType.rangePonts)}
                              />
                            </RadioGroup>
                          </Grid>

                          <Grid item xs={6} className={[classes.item, classes.itemTextField].join(' ')}>
                            {task.pointsFilterType === PointsFilterType.exactPonts ? (
                              <>
                                <div style={{ margin: '10px' }}>
                                  <TextField
                                    value={task.exactPoints}
                                    type="number"
                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                      handlePointsFilterChange(e.target.value, 'exactPoints', task.id)
                                    }
                                    label="Punkty"
                                    variant="outlined"
                                    fullWidth
                                    required
                                    error={!task.exactPoints && task.exactPoints !== 0}
                                    inputProps={{
                                      style: {
                                        padding: '8px',
                                        color: '#2C2D3E',
                                        fontSize: '14px',
                                        width: '80px',
                                      },
                                      min: 0,
                                      max: 100,
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    FormHelperTextProps={{
                                      style: {
                                        fontSize: '11px',
                                        color: '#BC0000',
                                        position: 'absolute',
                                        width: '130px',
                                        top: '35px',
                                        left: '-10px',
                                      },
                                    }}
                                    helperText={!task.exactPoints && task.exactPoints !== 0 ? errorMessage : ''}
                                  />
                                </div>
                                {task.exactPoints === 0 && (
                                  <>
                                    <Typography
                                      style={{
                                        fontSize: '13px',
                                        fontWeight: 'bold',
                                        display: 'inline',
                                        marginLeft: '40px',
                                      }}
                                    >
                                      {`Opcja "Brak odpowiedzi"`}
                                    </Typography>
                                    <div className={classes.TripleSwitch}>
                                      <TripleSwitch
                                        value={task.noAnswer === null ? -1 : task.noAnswer}
                                        possibleValues={[-1, 0, 1]}
                                        isAdvanceFilter={true}
                                        onChange={(value) => handleNoAnswerFilterChange(value, task.id)}
                                      />
                                    </div>
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                <div style={{ margin: '10px 40px 10px 10px' }}>
                                  <TextField
                                    value={task.pointsFrom ?? 0}
                                    type="number"
                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                      handlePointsFilterChange(e.target.value, 'pointsFrom', task.id)
                                    }
                                    label="Punkty od"
                                    variant="outlined"
                                    fullWidth
                                    required
                                    error={!task.pointsFrom && task.pointsFrom !== 0}
                                    inputProps={{
                                      style: {
                                        padding: '8px',
                                        color: '#2C2D3E',
                                        fontSize: '14px',
                                        width: '80px',
                                      },
                                      min: 0,
                                      max: 100,
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    FormHelperTextProps={{
                                      style: {
                                        fontSize: '11px',
                                        color: '#BC0000',
                                        position: 'absolute',
                                        width: '130px',
                                        top: '35px',
                                        left: '-10px',
                                      },
                                    }}
                                    helperText={!task.pointsFrom && task.pointsFrom !== 0 ? errorMessage : ''}
                                  />
                                </div>
                                <div style={{ margin: '10px' }}>
                                  <TextField
                                    value={task.pointsTo ?? 100}
                                    type="number"
                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                      handlePointsFilterChange(e.target.value, 'pointsTo', task.id)
                                    }
                                    label="Punkty do"
                                    variant="outlined"
                                    fullWidth
                                    required
                                    error={!task.pointsTo}
                                    inputProps={{
                                      style: {
                                        padding: '8px',
                                        color: '#2C2D3E',
                                        fontSize: '14px',
                                        width: '80px',
                                      },
                                      min: 0,
                                      max: 100,
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    FormHelperTextProps={{
                                      style: {
                                        fontSize: '11px',
                                        color: '#BC0000',
                                        position: 'absolute',
                                        width: '130px',
                                        top: '35px',
                                        left: '-10px',
                                      },
                                    }}
                                    helperText={!task.pointsTo ? errorMessage : ''}
                                  />
                                </div>
                              </>
                            )}
                          </Grid>
                        </Grid>
                        <hr className={classes.line} />
                      </>
                    )}
                    {task.examScoringSchemas.length > 0 && isExpandedTask(task.id) && isTaskChecked(task.id) && (
                      <>
                        <Typography className={classes.subtitle}>Lista kryteriów do zadania</Typography>
                        <hr className={classes.line} />
                        {task.examScoringSchemas.map((criterion) => {
                          return (
                            <Grid xs={12} key={`${task.id}_${criterion.id}`}>
                              <Grid xs={12}>
                                <>
                                  <Grid xs={12} className={[classes.item, classes.itemWithRadioGroup].join(' ')}>
                                    <Grid xs={2} className={classes.item}>
                                      <CheckboxOne
                                        fontSize={22}
                                        key={`${task.id}_${criterion.id}`}
                                        checked={isCriterionChecked(criterion.id)}
                                        onChange={() => handleCriterionCheckboxChange(criterion.id)}
                                      />
                                      <Typography style={{ marginRight: '30px', fontSize: '16px', fontWeight: 'bold' }}>
                                        {criterion.criterionNumber}
                                      </Typography>
                                    </Grid>
                                    {isCriterionChecked(criterion.id) ? (
                                      <>
                                        <Grid item xs={6}>
                                          <RadioGroup
                                            row
                                            aria-label="wybór typu filtra"
                                            name="filter-type-selector"
                                            value={criterion.pointsFilterType}
                                            onChange={(e) => {
                                              handleFilterTypeChange(
                                                parseInt(e.target.value) as PointsFilterType,
                                                task.id,
                                                criterion.id,
                                              );
                                            }}
                                          >
                                            <FormControlLabel
                                              value={PointsFilterType.exactPonts}
                                              control={<Radio />}
                                              label={getPointsFilterTypeLabel(PointsFilterType.exactPonts)}
                                            />
                                            <div style={{ display: 'flex' }}>
                                              <img
                                                src={Pipe}
                                                style={{ margin: '3px 15px 3px 0px' }}
                                                alt="ikona linia pionowa"
                                              />{' '}
                                            </div>
                                            <FormControlLabel
                                              value={PointsFilterType.rangePonts}
                                              control={<Radio />}
                                              label={getPointsFilterTypeLabel(PointsFilterType.rangePonts)}
                                            />
                                          </RadioGroup>
                                        </Grid>
                                        <Grid item xs={3} className={[classes.item, classes.itemTextField].join(' ')}>
                                          {criterion.pointsFilterType === PointsFilterType.exactPonts ? (
                                            <div style={{ margin: '10px' }}>
                                              <TextField
                                                value={criterion.exactPoints}
                                                type="number"
                                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                  handlePointsFilterChange(
                                                    e.target.value,
                                                    'exactPoints',
                                                    task.id,
                                                    criterion.id,
                                                  )
                                                }
                                                label="Punkty"
                                                variant="outlined"
                                                fullWidth
                                                required
                                                error={!criterion.exactPoints && criterion.exactPoints !== 0}
                                                inputProps={{
                                                  style: {
                                                    padding: '8px',
                                                    color: '#2C2D3E',
                                                    fontSize: '14px',
                                                    width: '80px',
                                                  },
                                                  min: 0,
                                                  max: 100,
                                                }}
                                                InputLabelProps={{ shrink: true }}
                                                FormHelperTextProps={{
                                                  style: {
                                                    fontSize: '11px',
                                                    color: '#BC0000',
                                                    position: 'absolute',
                                                    width: '130px',
                                                    top: '35px',
                                                    left: '-10px',
                                                  },
                                                }}
                                                helperText={
                                                  !criterion.exactPoints && criterion.exactPoints !== 0
                                                    ? errorMessage
                                                    : ''
                                                }
                                              />
                                            </div>
                                          ) : (
                                            <>
                                              <div style={{ margin: '10px 40px 10px 10px' }}>
                                                <TextField
                                                  value={criterion.pointsFrom ?? 0}
                                                  type="number"
                                                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                    handlePointsFilterChange(
                                                      e.target.value,
                                                      'pointsFrom',
                                                      task.id,
                                                      criterion.id,
                                                    )
                                                  }
                                                  label="Punkty od"
                                                  variant="outlined"
                                                  fullWidth
                                                  required
                                                  error={!criterion.pointsFrom && criterion.pointsFrom !== 0}
                                                  inputProps={{
                                                    style: {
                                                      padding: '8px',
                                                      color: '#2C2D3E',
                                                      fontSize: '14px',
                                                      width: '80px',
                                                    },
                                                    min: 0,
                                                    max: 100,
                                                  }}
                                                  InputLabelProps={{ shrink: true }}
                                                  FormHelperTextProps={{
                                                    style: {
                                                      fontSize: '11px',
                                                      color: '#BC0000',
                                                      position: 'absolute',
                                                      width: '130px',
                                                      top: '35px',
                                                      left: '-10px',
                                                    },
                                                  }}
                                                  helperText={
                                                    !criterion.pointsFrom && criterion.pointsFrom !== 0
                                                      ? errorMessage
                                                      : ''
                                                  }
                                                />
                                              </div>
                                              <div style={{ margin: '10px' }}>
                                                <TextField
                                                  value={criterion.pointsTo ?? 100}
                                                  type="number"
                                                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                    handlePointsFilterChange(
                                                      e.target.value,
                                                      'pointsTo',
                                                      task.id,
                                                      criterion.id,
                                                    )
                                                  }
                                                  label="Punkty do"
                                                  variant="outlined"
                                                  fullWidth
                                                  required
                                                  error={!criterion.pointsTo}
                                                  inputProps={{
                                                    style: {
                                                      padding: '8px',
                                                      color: '#2C2D3E',
                                                      fontSize: '14px',
                                                      width: '80px',
                                                    },
                                                    min: 0,
                                                    max: 100,
                                                  }}
                                                  InputLabelProps={{ shrink: true }}
                                                  FormHelperTextProps={{
                                                    style: {
                                                      fontSize: '11px',
                                                      color: '#BC0000',
                                                      position: 'absolute',
                                                      width: '130px',
                                                      top: '35px',
                                                      left: '-10px',
                                                    },
                                                  }}
                                                  helperText={!criterion.pointsTo ? errorMessage : ''}
                                                />
                                              </div>
                                            </>
                                          )}
                                        </Grid>
                                      </>
                                    ) : (
                                      <Typography style={{ fontSize: '14px' }}>
                                        Aby ustawić filtrowanie dla tego kryterium, zaznacz checkbox
                                      </Typography>
                                    )}
                                  </Grid>
                                  <hr className={classes.line} />
                                </>
                              </Grid>
                            </Grid>
                          );
                        })}
                      </>
                    )}
                  </>
                </Grid>
              </Grid>
            );
          })}
      </Grid>
      <div style={{ position: 'relative', top: '58px', left: '330px', cursor: 'pointer' }}>
        <ButtonOne
          startIcon={<CloseIcon />}
          onClick={() => {
            props.onAdvancedFilterReset();
          }}
        >
          Wyczyść filtrowanie
        </ButtonOne>
      </div>
    </ModalWithButtons>
  );
};
